<script lang="ts">
	import { BROWSER } from "esm-env";
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	import Input from "$form/Fields/Shared/Input.svelte";
	import Label from "$form/Fields/Shared/Label.svelte";
	import Description from "$form/Fields/Shared/Description.svelte";
	import { createEventDispatcher, onMount, getContext } from "svelte";
	import type { Writable } from "svelte/store";
	import { browser } from "$app/environment";

	const styling = getContext<Styling>("styling");
	const dispatch = createEventDispatcher();
	const visit = getContext<Writable<Visit>>("visit");
	const readOnlyValues = getContext<Writable<FormValues>>("readOnlyValues");
	const debug = getContext<Writable<FormResponseDebug[]>>("debug");

	export let field: DropdownFieldDisplay;
	export let value: ChoiceValue[];

	let resetOptions: CallbackFn;
	let optionsCoverRef: HTMLDivElement;
	let options: ChoiceOption[];
	let max: string = "1";
	let loading: boolean = false;
	let localCache: {
		[key: string]: {
			total: number;
			data: ChoiceOption[];
		};
	} = {};
	let clearLocalCacheTimeout: ReturnType<typeof setTimeout> | undefined;
	let elementWidth: number = 0;
	let remote: {
		url: string;
		method: string;
		pagination: boolean;
		search: boolean;
	} | null = null;
	let mounted: boolean = false;
	let fetchAbort = new AbortController();
	let fetchSignal = fetchAbort.signal;

	$: localCacheChange(localCache);
	$: optionsList = field.options.list;
	$: updateOptions(optionsList);

	const updateOptions = (list: ChoiceOption[]) => {
		options =
			field.options.source === "list"
				? field.settings.groups
					? list.map((option) => {
							return {
								...option,
								description: field.settings.descriptions
									? option.description
									: undefined
							};
						})
					: list.map((option) => {
							return {
								...option,
								description: field.settings.descriptions
									? option.description
									: undefined,
								group: undefined
							};
						})
				: [];
	};

	const localCacheChange = (newLocalCache: typeof localCache) => {
		if (!browser) return;
		if (clearLocalCacheTimeout) {
			clearTimeout(clearLocalCacheTimeout);
		}
		if (Object.keys(localCache).length > 0) {
			clearLocalCacheTimeout = setTimeout(() => {
				localCache = {};
				clearLocalCacheTimeout = undefined;
			}, 1000 * 5);
		}
	};

	const isValidUrl = (url: string) => {
		try {
			return Boolean(new URL(url));
		} catch (e) {
			return false;
		}
	};

	const optionsFunction = async (
		skip: number | null,
		take: number | null,
		search: string
	): Promise<{
		total: number;
		data: ChoiceOption[];
		error?: string | null;
	}> => {
		let baseResponse: {
			total: number;
			data: ChoiceOption[];
			error?: string | null;
		} = {
			total: 0,
			data: [],
			error: null
		};

		let resultObject = {
			total: 0,
			data: []
		};

		// Check if valid URL
		if (!remote?.url || !isValidUrl(remote.url)) {
			baseResponse.error = "Invalid URL";
			return baseResponse;
		}

		if (loading) {
			fetchAbort.abort("Abort previous fetch");
			fetchAbort = new AbortController();
			fetchSignal = fetchAbort.signal;
			loading = false;
		}

		const url = new URL(remote.url);
		if (skip !== null) {
			url.searchParams.set("skip", skip.toString());
		}
		if (take !== null) {
			url.searchParams.set("take", take.toString());
		}
		if (skip !== null && take !== null) {
			url.searchParams.set("page", (1 + Math.floor(skip / take)).toString());
		}
		if (search) url.searchParams.set("s", search);

		if (localCache[url.toString()]) {
			return {
				total: localCache[url.toString()].total,
				data: localCache[url.toString()].data
			};
		}

		loading = true;
		let response;
		try {
			response = await fetch(url.toString(), {
				method: remote.method,
				signal: fetchSignal,
				body:
					remote.method === "POST"
						? JSON.stringify($readOnlyValues)
						: undefined,
				headers: {
					"fly-prefer-region": import.meta.env.PUBLIC_FLY_PRIMARY_REGION
				}
			});
		} catch (e) {
			return;
		}
		loading = false;

		if (!response.ok) {
			baseResponse.error = "Invalid URL";
			return baseResponse;
		}
		const responseData = await response.json();
		if (responseData.error) {
			$debug.push({
				type: "error",
				message: responseData.error
			});
			$debug = $debug;
		}

		if (typeof responseData !== "object") {
			baseResponse.error = "Invalid URL";
			return baseResponse;
		}
		if (typeof responseData.total === "undefined") {
			resultObject = {
				total: responseData.length,
				data: responseData.data
			};
		} else {
			resultObject = responseData;
		}

		// Check if resultObject is an array of strings
		if (
			Array.isArray(resultObject.data) &&
			typeof resultObject.data[0] === "string"
		) {
			const finalResult = {
				total: resultObject.total ?? null,
				data: resultObject.data.map((item) => ({ value: item, label: item }))
			};
			localCache = {
				[url.toString()]: finalResult
			};
			return finalResult;
		}

		// Check if data is an array of objects
		let tempData: ChoiceOption[] = resultObject.data;
		if (
			Array.isArray(tempData) &&
			typeof tempData[0] === "object" &&
			tempData[0].value
		) {
			const finalResult = {
				total: resultObject.total,
				data: resultObject.data.map((item: ChoiceOption) => ({
					value: item.value,
					label: item.label,
					description: item.description ?? undefined,
					group: item.group ?? undefined,
					embed: item.embed ?? undefined
				}))
			};
			localCache = {
				[url.toString()]: finalResult
			};
			return finalResult;
		}

		baseResponse.error = "Invalid response";
		return baseResponse;
	};

	const setRemoteUrl = () => {
		let tempRemoteUrl: string | undefined;
		let pagination = false;
		let search = false;
		let method = "GET";
		if (field.options.source === "remoteUrl") {
			tempRemoteUrl = `${
				import.meta.env.PUBLIC_APP_URL
			}/public/form/dropdown/url?form_id=${$visit.form_id}&field_id=${
				field.id
			}&test=${$visit.test}&url=${encodeURIComponent(
				field.options.remoteString
			)}&auth=${Boolean(field.options.remoteUrl.enableAuthHeader)}&test=${$visit.test}`;
			pagination = field.options.remoteUrl?.pagination ?? false;
			search = field.options.remoteUrl?.search ?? false;
		} else if (field.options.source === "googleSheets") {
			tempRemoteUrl = `${
				import.meta.env.PUBLIC_APP_URL
			}/public/form/dropdown/google-sheets?form_id=${$visit.form_id}&field_id=${
				field.id
			}&test=${$visit.test}`;
			pagination = false;
			search = false;
		} else if (field.options.source === "salesforce") {
			tempRemoteUrl = `${
				import.meta.env.PUBLIC_APP_URL
			}/public/form/dropdown/salesforce?form_id=${$visit.form_id}&field_id=${
				field.id
			}&test=${$visit.test}`;
			pagination = true;
			search = field.options.salesforce.type === "picklist" ? false : true;
			method = "POST";
		} else {
			remote = null;
		}
		if (tempRemoteUrl && remote?.url !== tempRemoteUrl) {
			remote = {
				url: tempRemoteUrl,
				method,
				pagination,
				search
			};
		}
	};

	$: if (BROWSER || remoteString) setRemoteUrl();

	$: max =
		field.settings.multiple && field.settings.max ? field.settings.max : "1";
	$: remoteString = field.options.remoteString;
	$: changeRemoteString(remoteString);

	let Component;

	const changeRemoteString = (_newRemoteString: string) => {
		if (
			BROWSER &&
			field.options.source !== "list" &&
			typeof _newRemoteString !== "undefined" &&
			_newRemoteString !== "" &&
			mounted
		) {
			localCache = {};
			value = [];
		}
	};

	onMount(async () => {
		mounted = true;
		if (BROWSER) {
			Component = await import("$basic/Select");
		}
		if (optionsCoverRef) {
			optionsCoverRef.addEventListener("click", () => {
				dispatch("updateOther", {
					active: true
				});
			});
		}
	});
</script>

<div
	class="field-label-cover"
	class:has-placeholder="{styling.placeholders !== false}">
	{#if field.labelHTMLComputed && field.settings.hideLabel !== true}
		{#if styling.placeholders === false}
			<Label html="{field.labelHTMLComputed}" />
		{/if}
		{#if field.descriptionHTMLComputed}
			<Description html="{field.descriptionHTMLComputed}" />
		{/if}
	{/if}
</div>
<div
	bind:this="{optionsCoverRef}"
	bind:clientWidth="{elementWidth}"
	class="field-input-cover">
	<div style="display: flex; align-items: center; width: 100%">
		{#if Component}
			{#key remoteString}
				<svelte:component
					this="{Component.Select}"
					bind:value="{value}"
					bind:resetOptions="{resetOptions}"
					textSize="base"
					max="{max ? parseInt(max) : undefined}"
					fixedWidth="{Math.max(elementWidth, 260)}"
					class="field-input"
					fancyPlacement="{false}"
					showAllGroups="{false}"
					cross="{false}"
					optionsFn="{remote ? optionsFunction : undefined}"
					remotePagination="{remote?.pagination ?? false}"
					remoteSearch="{remote?.search ?? false}"
					placeholder="{styling.placeholders
						? field.labelComputed
						: (field.styling?.placeholder ?? '(select)')}"
					options="{options}" />
			{/key}
		{:else}
			<Input
				type="text"
				name="{field.id}"
				value=""
				placeholder="{styling.placeholders ? field.labelComputed : ''}"
				label="{field.labelComputed}" />
		{/if}
		{#if loading}
			<div class="field-icon">
				<div style="width: 0.75rem; height: 0.75rem" class="loader"></div>
			</div>
		{:else}
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				width="15"
				height="15"
				class="field-icon"
				viewBox="0 0 24 24"
				stroke-width="2"
				stroke="currentColor">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					d="M19.5 8.25l-7.5 7.5-7.5-7.5"></path>
			</svg>
		{/if}
	</div>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
